/**
 * /e/$entity — the generic entity browser, on the shared <ListView>. Any
 * registered entity gets the advanced list (grid bar · view tabs · filter chips
 * · column ⋮ menu · sort · inline edit · ⌘K) via ListView's field_meta fallback
 * view — no per-entity core.view registration required.
 *
 * Christopher, 2026-08-06: "detail record not opening next to the list." It was
 * not — this route NAVIGATED to `/e/$entity/$id`, throwing the list away to show
 * one of its rows, while every bespoke desk in the console opens a pane over the
 * grid. Now it peeks like the rest of them, and the pane is the same
 * `RecordBody` the full page renders, so the two cannot drift.
 *
 * The full page is still there and still reachable — the pane header links to
 * it, and it is what a deep link renders. What changed is which one you get by
 * clicking a row you are in the middle of reading.
 */
import { useMemo } from 'react'
import { useParams, useNavigate, useSearch, Link } from '@tanstack/react-router'
import type { Row } from '@solum/sdk'
import { singularize } from '@solum/experience'
import { useEntityIdentities, useSchema } from '../../data/queries'
import { ListView, type ListConfig } from '../../list/ListView'
import { writeDoorOf, writeDoorReason } from '../../data/writeState'
import { RecordBody } from '../../record/RecordBody'
import { setTrail } from '../../record/trail'
import { PeekHeader, PeekBody } from '@solum/ui'

export function EntityList() {
  const { entity } = useParams({ from: '/e/$entity' })
  const { peek } = useSearch({ from: '/e/$entity' }) as { peek?: string }
  const navigate = useNavigate()
  const schema = useSchema()
  const identities = useEntityIdentities()
  const navigationEligible = (identities.data ?? [])
    .find((identity) => identity.entity_type === entity)?.navigation_eligible === true
  const label = ((schema.data?.entities ?? []) as { key: string; label: string }[]).find((e) => e.key === entity)?.label ?? entity
  const sing = singularize(label)
  // The metamodel already says whether this entity can be written directly, and
  // the console already has it in hand — `describe_schema` sends every column of
  // `core.entity_type`. Offering ＋New over an `rpc` door leads to a form whose
  // Save is answered 403 for everybody (#877).
  const readOnly = writeDoorOf(schema.data, entity) === 'rpc'

  const openPeek = (id: string | undefined) => {
    if (id && !navigationEligible) return
    void navigate({ to: '/e/$entity', params: { entity }, search: id ? { peek: id } : {} })
  }

  const config: ListConfig = useMemo(() => ({
    entity, routePath: `/e/${entity}`, title: label, label: sing,
    eyebrow: entity, columns: {},
    readOnly,
    readOnlyReason: readOnly ? writeDoorReason(sing) : undefined,
    onNew: navigationEligible
      ? () => void navigate({ to: '/e/$entity/$id', params: { entity, id: 'new' } })
      : undefined,
    peek: navigationEligible ? (row, ctx) => (
      <RecordPeek entity={entity} row={row} pluralLabel={label}
        orderedIds={ctx.orderedIds} onNavigate={ctx.navigate} onClose={ctx.close} />
    ) : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [entity, label, navigationEligible, readOnly])

  return <ListView chrome="page" config={config} peekId={peek} onPeek={openPeek} />
}

/**
 * The record, in the pane beside the grid.
 *
 * The header is the desks' own (`.phead`): ↑/↓ walk the list without closing the
 * pane, and the position counter says where in it you are — the affordance that
 * makes a pane better than a page for reading through a list of records. "Open
 * the full record" sits next to them, because the page still carries the two
 * PARTY surfaces — the richer reading for `core_person` / `core_organization` —
 * which a pane is not where you go to study.
 *
 * RELATED ITEMS ride the pane (2026-08-08). They did not, and that was a
 * regression rather than a decision: `relations` was introduced as opt-in while
 * the page rendered the cascade ITSELF, so "off by default" meant "the page has
 * it". The moment the cascade moved into `RecordBody`, the default started
 * meaning "nobody has it", and this caller — the generic browser's peek, which
 * every entity without a hand-built desk lands on — silently lost it while the
 * list's own related-pane kept it. Two panes, one prop, one of them passing it.
 *
 * The old reason for withholding was width: the peek was a fixed 500px overlay.
 * It is now a resizable column of the split, which is exactly what removed that
 * argument for `ListView`'s pane and removes it here for the same reason.
 */
function RecordPeek({ entity, row, pluralLabel, orderedIds, onNavigate, onClose }: {
  entity: string; row: Row; pluralLabel: string
  orderedIds: string[]; onNavigate: (id: string) => void; onClose: () => void
}) {
  const id = String(row.id)
  return (<>
    <PeekHeader pager={{ ids: orderedIds, current: id, onNavigate, subject: 'record' }} onClose={onClose}
      actions={(
        <Link to="/e/$entity/$id" params={{ entity, id }} aria-label="Open the full record" title="Open the full record"
          onClick={() => setTrail(`/e/${entity}/${id}`, [{ label: pluralLabel, to: `/e/${entity}` }])}>⤢</Link>
      )} />
    <PeekBody>
      <RecordBody entity={entity} id={id} presentation="pane" relations headingTestid="peek-heading"
        onSaved={({ intent }) => { if (intent === 'close') onClose() }} />
    </PeekBody>
  </>)
}
